/**
 * Canonical `--Key--` mail merge (London Smoke & Cure).
 * Same rules as `emailtransporter.js` send path so Template Studio preview,
 * Newsletter Editor preview/send, and Cloud Run payloads stay aligned.
 *
 * Key pattern: word chars, spaces, `.`, `:`, `,`, `-` (matches Email Template Studio + transporter).
 */

const PLACEHOLDER_BODY_SOURCE = String.raw`([\w .:,-]+)`;

/**
 * Resolve merge value: exact key first, then case-insensitive match (list rows / Firestore keys vary).
 * @param {Record<string, unknown>|null|undefined} data
 * @param {string} trimmedKey
 * @returns {unknown|undefined}
 */
function getMergeValue(data, trimmedKey) {
    if (data == null) return undefined;
    const direct = data[trimmedKey];
    if (direct != null && direct !== '') return direct;
    const lower = trimmedKey.toLowerCase();
    for (const k of Object.keys(data)) {
        if (k.toLowerCase() === lower) {
            const v = data[k];
            if (v != null && v !== '') return v;
        }
    }
    return undefined;
}

export function mergeDataFillsKey(data, key) {
    const v = getMergeValue(data, String(key || '').trim());
    return v != null && String(v) !== '';
}

/**
 * After regex-based merge: replace any literal `--key--` still present (Quill/HTML can break the main pattern).
 * @param {string} content
 * @param {Record<string, unknown>} data
 * @returns {string}
 */
export function replaceRemainingLiteralPlaceholders(content, data) {
    if (data == null || content == null) return String(content ?? '');
    let out = String(content);
    const keys = Object.keys(data).sort((a, b) => b.length - a.length);
    for (const k of keys) {
        const v = data[k];
        if (v == null || v === '') continue;
        const token = `--${k}--`;
        if (out.includes(token)) {
            out = out.split(token).join(String(v));
        }
    }
    return out;
}

/**
 * @param {string} content
 * @returns {string[]} unique placeholder keys (trimmed) in document order of first occurrence
 */
export function extractEmailPlaceholders(content) {
    const s = String(content ?? '');
    const re = new RegExp(`--${PLACEHOLDER_BODY_SOURCE}--`, 'g');
    const out = [];
    const seen = new Set();
    let m;
    while ((m = re.exec(s)) !== null) {
        const key = m[1].trim();
        const low = key.toLowerCase();
        if (!seen.has(low)) {
            seen.add(low);
            out.push(key);
        }
    }
    return out;
}

/**
 * @param {string} content
 * @param {Record<string, unknown>} data
 * @returns {{ content: string, placeholderUsage: { used: string[], missing: string[], totalCount: number } }}
 */
export function replaceEmailPlaceholdersWithUsage(content, data) {
    const used = [];
    const missing = [];
    const allPlaceholders = extractEmailPlaceholders(content);
    const re = new RegExp(`--${PLACEHOLDER_BODY_SOURCE}--`, 'g');
    const result = String(content ?? '').replace(re, (match, key) => {
        const trimmedKey = key.trim();
        const v = getMergeValue(data, trimmedKey);
        if (v != null && v !== '') {
            if (!used.includes(trimmedKey)) used.push(trimmedKey);
            return String(v);
        }
        if (!missing.includes(trimmedKey)) missing.push(trimmedKey);
        return match;
    });

    return {
        content: result,
        placeholderUsage: {
            used,
            missing,
            totalCount: allPlaceholders.length,
        },
    };
}

/**
 * @param {string} content
 * @param {Record<string, unknown>} data
 * @returns {string}
 */
export function replaceEmailPlaceholders(content, data) {
    return replaceEmailPlaceholdersWithUsage(content, data).content;
}

/**
 * Regex merge + literal `--key--` pass (handles cases where the main pattern misses contiguous tokens in HTML).
 * @param {string} content
 * @param {Record<string, unknown>} data
 * @returns {string}
 */
export function replaceEmailPlaceholdersFully(content, data) {
    return replaceRemainingLiteralPlaceholders(replaceEmailPlaceholders(content, data), data);
}

/**
 * Remaining `--token--` scan that does not glue adjacent tokens
 * (`--chilledProductNote----itemsHtml--` stays two keys).
 */
const REMAINING_TOKEN_RE = /--((?:(?!--)[\w .:,-])+?)--/g;

/**
 * After merge: any remaining `--token--` blocks mean the email must not send.
 *
 * @param {string} [subject]
 * @param {string} [html]
 * @returns {string[]} unique literal tokens still present (e.g. `--First Name--`)
 */
export function extractRemainingMergeTokensAfterMerge(subject, html) {
    const text = `${subject || ''}\n${html || ''}`;
    const out = [];
    const seen = new Set();
    const re = new RegExp(REMAINING_TOKEN_RE.source, 'g');
    let m;
    while ((m = re.exec(text)) !== null) {
        const tok = m[0];
        const low = tok.toLowerCase();
        if (!seen.has(low)) {
            seen.add(low);
            out.push(tok);
        }
    }
    return out;
}

/**
 * Merge subject, text, and html against sample or send-time data, then list any `--key--` still present.
 * Studio preview, test send, and Publish must use this so a missing sample field cannot look resolved.
 *
 * @param {{ subject?: string, text?: string, html?: string }} bodies
 * @param {Record<string, unknown>|null|undefined} data
 * @returns {string[]}
 */
export function findUnresolvedMergeTokensAfterSample(bodies, data) {
    const subject = replaceEmailPlaceholdersFully(bodies?.subject || '', data || {});
    const text = replaceEmailPlaceholdersFully(bodies?.text || '', data || {});
    const html = replaceEmailPlaceholdersFully(bodies?.html || '', data || {});
    return extractRemainingMergeTokensAfterMerge(subject, `${html}\n${text}`);
}

/**
 * @param {string[]} tokens
 * @returns {string}
 */
export function unresolvedMergeTokensMessage(tokens) {
    const list = (tokens || []).join(', ');
    return `This letter still has placeholders the sample (or send data) cannot fill: ${list}. It will not send like this. Remove those tokens or use a key that is filled in the preview.`;
}

/**
 * @param {string} [subject]
 * @param {string} [html]
 * @returns {boolean}
 */
export function hasRemainingMergeTokensAfterMerge(subject, html) {
    return extractRemainingMergeTokensAfterMerge(subject, html).length > 0;
}
